import { useEffect, useMemo } from "react";
import Head from "next/head";
import Image from "next/image";
import { commify, formatEther } from "ethers/lib/utils";
import { BigNumber } from "ethers";
import { useAccount, useContractRead } from "wagmi";
import { Cell, useExpanded, useTable } from "react-table";
import {
  Flex,
  Text,
  Box,
  Spinner,
  Divider,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  useColorMode,
  Center,
  useColorModeValue,
  Button,
  Show,
} from "@chakra-ui/react";
import { AiOutlineInfoCircle } from "react-icons/ai";

// Constants
import { vaultColumns, vaults } from "constants/vaults";
import { VAULTS_LET_US_KNOW } from "constants/constants";
import { LET_US_KNOW_CTA } from "constants/analytics";

// Config
import { config, currentNetworkInfo } from "config/network";
import { vaultsConfig } from "config/vaults";

// Hooks
import useMounted from "hooks/useMounted";
import useApp from "hooks/useApp";
import useConverstionRates from "hooks/useConverstionRates";
import useVaults from "hooks/useVaults";

// Utils
import { toFixedWithoutRounding, toStringPrecision } from "utils/common";

// Components
import Tooltip from "components/Tooltip";
import DeFiStrategy from "components/DeFiStrategy";

// Images
import messageLightIcon from "assets/svg/message.svg";
import messageDarkIcon from "assets/svg/message-dark.svg";

const PageHead = () => {
  return (
    <Head>
      <title>Portfolios | Stader</title>
    </Head>
  );
};

const TVL_CAP = 10000;
const WALLET_CAP = 1000;

const Portfolios = () => {
  const { address } = useAccount();
  const { colorMode } = useColorMode();
  const isMounted = useMounted();
  const { pendingTxnCount, track } = useApp();

  const { conversionToBnb } = useConverstionRates();

  const { totalTVL, apyInfo, refectch } = useVaults();

  const messageIcon = useColorModeValue(messageLightIcon, messageDarkIcon);

  const { data: mySharesInBnbx, refetch: userSharesInBnbxRefetch } =
    useContractRead({
      address: vaultsConfig[vaults[0].key].address,
      abi: vaultsConfig[vaults[0].key].abi,
      functionName: "getPortfolioValueInBnbX",
      chainId: +config.chainId,
      args: [address],
      enabled: !!address,
      structuralSharing: true,
    });

  useEffect(() => {
    refectch();
    userSharesInBnbxRefetch();
  }, [pendingTxnCount, refectch, userSharesInBnbxRefetch]);

  const tokenSymbol = config.nativeCurrency.symbol;

  const logo = currentNetworkInfo.icon[colorMode];

  const vaultData = useMemo(() => vaults, []);

  const columns = useMemo(
    () => vaultColumns(colorMode, apyInfo),
    [colorMode, apyInfo]
  );

  const tableInstance = useTable({ columns, data: vaultData }, useExpanded);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
  } = tableInstance;

  if (!isMounted || !totalTVL || !mySharesInBnbx || !conversionToBnb) {
    <Flex alignItems="center" justifyContent="center" h="500px">
      <Spinner size="lg" color="brand.primary" />
    </Flex>;
  }

  const currentValue = useMemo(
    () =>
      mySharesInBnbx && conversionToBnb
        ? +toFixedWithoutRounding(
            toStringPrecision(
              parseFloat(formatEther(BigNumber.from(mySharesInBnbx))) *
                conversionToBnb
            ),
            4
          )
        : 0,
    [conversionToBnb, mySharesInBnbx]
  );

  return (
    <>
      <PageHead />
      <Box p={{ base: 0, lg: "1rem" }} pt="0">
        <Flex gap="1rem">
          <Flex
            bg="brand.backgroundLight"
            borderRadius="50%"
            h={{ base: "fit-content", xl: "62px" }}
            w={{ base: "fit-content", xl: "62px" }}
            alignItems="center"
            justifyContent="center"
          >
            <Image src={logo} width={62} height={62} alt="binance-logo" />
          </Flex>

          <Box>
            <Flex align="center">
              <Text
                fontSize={{ base: "16px", lg: "24px" }}
                lineHeight={{ base: "24px", lg: "28px" }}
                fontFamily="medium"
              >
                BNBx Portfolios
              </Text>
              <Text
                ml="4px"
                fontSize={{ base: "12px", lg: "14px" }}
                fontFamily="medium"
              >
                (capped launch)
              </Text>
              <Tooltip
                label={
                  <>
                    <Text>Total Deposit Cap = 10K BNB,</Text>
                    <Text> Per Wallet Cap = 1K BNB</Text>
                  </>
                }
              >
                <Text ml="4px" fontSize="14px">
                  <AiOutlineInfoCircle />
                </Text>
              </Tooltip>
            </Flex>

            <Text
              mt="0.5rem"
              fontSize={{ base: "12px", lg: "1rem" }}
              color="brand.textInactive"
            >
              Automated DeFi Strategies for you to choose based on your risk
              profile.
            </Text>
          </Box>
        </Flex>

        <Flex
          bg="brand.backgroundLight"
          borderRadius="8px"
          p="1rem"
          mt="2.25rem"
          textAlign="center"
          flexDir={{ base: "column", xl: "row" }}
          justifyContent="space-around"
        >
          <Flex
            flexGrow={1}
            flexDir={{ base: "row", xl: "column" }}
            justifyContent="space-between"
            alignItems="center"
            gap="1rem"
            py={{ base: "0.5rem", xl: "0" }}
          >
            <Flex alignItems="center">
              <Text fontSize="14px">TVL</Text>
              <Tooltip label="Total BNB deposited in Portfolios">
                <Text ml="4px" fontSize="14px">
                  <AiOutlineInfoCircle />
                </Text>
              </Tooltip>
            </Flex>
            <Text fontFamily="semi-bold">
              {totalTVL.toFixed(0) || "0"} {tokenSymbol}
            </Text>
          </Flex>
          <Flex
            height="32px"
            position="relative"
            top="1rem"
            display={{ base: "none", xl: "block" }}
          >
            <Divider orientation="vertical" borderColor="brand.separator" />
          </Flex>
          <Flex
            flexGrow={1}
            flexDir={{ base: "row", xl: "column" }}
            justifyContent="space-between"
            alignItems="center"
            gap="1rem"
            py={{ base: "0.5rem", xl: "0" }}
          >
            <Flex alignItems="center">
              <Text fontSize="14px">My Current Value</Text>
              <Tooltip label="Total BNB deposited by you in Portfolios + Yields Earned">
                <Text ml="4px" fontSize="14px">
                  <AiOutlineInfoCircle />
                </Text>
              </Tooltip>
            </Flex>
            <Text fontFamily="semi-bold">
              {commify(currentValue)} {tokenSymbol}
            </Text>
          </Flex>
          <Flex
            height="32px"
            position="relative"
            top="1rem"
            display={{ base: "none", xl: "block" }}
          ></Flex>
        </Flex>
        <Table
          mt={{ base: "1rem", lg: "2.5rem" }}
          {...getTableProps()}
          className="table-spaced"
        >
          <Thead>
            {headerGroups.map((headerGroup) => (
              <Tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                {headerGroup.headers.map((column, index) => (
                  <Th
                    {...column.getHeaderProps()}
                    key={column.id}
                    fontSize={{ base: 10, lg: 16 }}
                    pb="0"
                    textTransform="none"
                    color="brand.textInactive"
                    fontWeight="normal"
                    fontFamily={index > 1 ? "regular" : "semi-bold"}
                  >
                    {column.render("Header")}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody fontSize={{ base: 10, lg: 16 }} {...getTableBodyProps()}>
            {rows.map((row: any) => {
              prepareRow(row);
              return (
                <>
                  <Tr
                    {...row.getRowProps()}
                    key={row.original.key}
                    bg="brand.backgroundLight"
                    className={row.isExpanded ? "expanded" : ""}
                    fontFamily="semi-bold"
                  >
                    {row.cells.map((cell: Cell) => {
                      return (
                        <Td
                          {...cell.getCellProps()}
                          key={`${cell.column.id}-${cell.row.id}`}
                        >
                          {cell.render("Cell")}
                        </Td>
                      );
                    })}
                  </Tr>
                  {row.isExpanded ? (
                    <Tr pos="relative" top="-20px">
                      <Td
                        colSpan={visibleColumns.length}
                        borderRadius="0px !important"
                        bg="brand.backgroundLight"
                        borderBottomRadius="0.5rem !important"
                      >
                        <DeFiStrategy
                          vault={row}
                          isTVLLimitReached={totalTVL >= TVL_CAP}
                          isWalletLimitReached={currentValue >= WALLET_CAP}
                        />
                      </Td>
                    </Tr>
                  ) : null}
                </>
              );
            })}
          </Tbody>
        </Table>
        <Center
          pt={{ base: "1rem", lg: "5rem" }}
          flexDir="column"
          gap="1rem"
          fontSize={{ base: 12, lg: 14 }}
        >
          <Show above="xl">
            <Image
              src={messageIcon.src}
              width={52}
              height={42}
              alt="message-icon"
            />
          </Show>
          <Show below="xl">
            <Image
              src={messageIcon.src}
              width={40}
              height={42}
              alt="message-icon"
            />
          </Show>

          <Show above="xl">
            <Text>
              More Strategies coming soon. Which strategies would you like to
              see?
            </Text>
          </Show>
          <Show below="xl">
            <Box textAlign="center">
              <Text>More Strategies coming soon.</Text>
              <Text>Which strategies would you like to see?</Text>
            </Box>
          </Show>

          <Button
            as="a"
            href={VAULTS_LET_US_KNOW}
            target="_blank"
            rel="noreferrer"
            variant="outline"
            borderColor="brand.textColor"
            size={{ base: "sm", lg: "md" }}
            _hover={{
              color: "black",
              bg: "brand.primary",
            }}
            onClick={() => track(LET_US_KNOW_CTA)}
          >
            <Text fontSize={{ base: 10, lg: 16 }}>Let us know</Text>
          </Button>
        </Center>
      </Box>
    </>
  );
};

export default Portfolios;
